/**
 * Team-colour stripe legibility.
 *
 * Most of the NFL's primaries are near-black — six clubs sit on #101820 or
 * darker — so painting a raw club colour onto a dark surface produces a smudge
 * rather than a cue. This lifts those colours toward cream in small steps until
 * they clear a minimum contrast ratio against whatever surface they sit on,
 * which keeps navy reading as navy and forest as green instead of replacing
 * them with a flat hairline.
 *
 * The surface is a PARAMETER, not a constant. Contrast is a ratio, so a stripe
 * tuned against the resting pick button (surface-3) does not automatically
 * clear on the darker chip background (surface-2): Pittsburgh's #101820 scores
 * 1.44 on the former and 1.23 on the latter. Callers pass the surface they
 * actually render on.
 *
 * Extracted from game-card.tsx, which owned the only copy — the used-teams
 * strip then painted raw primaries and lost the cue on exactly those clubs.
 */

/** WCAG relative luminance of a #rrggbb hex (0 = black, 1 = white). */
export function relativeLuminance(hex: string): number | null {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const n = parseInt(hex.slice(1), 16);
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin((n >> 16) & 0xff) + 0.7152 * lin((n >> 8) & 0xff) + 0.0722 * lin(n & 0xff);
}

/** Resting pick-button background (bg-surface-3, globals.css). */
export const SURFACE_3 = '#33302B';
/** Chip / used-team background (bg-surface-2) — darker, so it lifts harder. */
export const SURFACE_2 = '#272320';

/** #F5F0E8 @ 40% — last-resort stand-in for an unparseable palette entry. */
export const CREAM_HAIRLINE = 'rgba(245, 240, 232, 0.4)';

const CREAM_RGB = [245, 240, 232] as const;

/**
 * Minimum WCAG contrast a stripe needs against its surface. Below this a 3-4px
 * band reads as a smudge. Deliberately not the 3:1 non-text minimum — these are
 * decorative cues, not information, and 3:1 would strip the colour off most of
 * the league.
 */
export const MIN_STRIPE_CONTRAST = 1.4;

function contrastBetween(a: number, b: number): number {
  const [lighter, darker] = a > b ? [a, b] : [b, a];
  return (lighter + 0.05) / (darker + 0.05);
}

/** Blend a hex toward cream by `amount` (0-1), returning #rrggbb. */
function liftToward(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  const mixed = ch.map((c, i) => Math.round(c + (CREAM_RGB[i] - c) * amount));
  return `#${mixed.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * A club colour that is legible against `surface`, preserving hue where it can.
 * Returns the cream hairline only when the input cannot be parsed or no amount
 * of lifting clears the bar.
 */
export function stripeColor(hex: string, surface: string = SURFACE_3): string {
  const l = relativeLuminance(hex);
  if (l === null) return CREAM_HAIRLINE;
  const surfaceL = relativeLuminance(surface) ?? 0;
  if (contrastBetween(l, surfaceL) >= MIN_STRIPE_CONTRAST) return hex;
  for (let amount = 0.15; amount <= 1; amount += 0.15) {
    const lifted = liftToward(hex, amount);
    const ll = relativeLuminance(lifted);
    if (ll !== null && contrastBetween(ll, surfaceL) >= MIN_STRIPE_CONTRAST) return lifted;
  }
  return CREAM_HAIRLINE;
}
